import { Secret } from '../OrchestrationSecrets/types';

export enum FabricOAuthType {
  U2M = 'u2m',
  M2M = 'm2m'
}

export enum ORCHESTRATION_PROVIDER {
  Databricks = 'databricks',
  Snowflake = 'snowflake',
  ProphecyManaged = 'ProphecyManaged',
  BigQuery = 'bigquery'
}

export enum OrchestrationDbxAuthType {
  PAT = 'pat',
  OAUTH = 'oauth',
  OIDC = 'oidc'
}

export type OrchestratorOidcAuthProperties<SecretPayloadType extends SecretPayload = SecretPayload> = {
  authServerUrl: string;
  resource: string;
  clientId: string;
  // clientSecret and the certificate/privateKey pair are alternatives - only one group is ever sent
  clientSecret?: SecretPayloadType;
  certificate?: SecretPayloadType;
  privateKey?: SecretPayloadType;
};

export enum ProphecyManagedComputeSize {
  X1 = '1x',
  X2 = '2x',
  X3 = '3x',
  X4 = '4x'
}

export const DEFAULT_COMPUTE_SIZE = ProphecyManagedComputeSize.X1;

export type OrchestrationFabricResponse = {
  fabricID: string;
  name: string;
  teamID: string;
  properties?: { computeSize?: ProphecyManagedComputeSize };
};

export type OrchestrationFabricInfo = {
  url: string;
  computeSize?: ProphecyManagedComputeSize;
};

export type OrchestrationDataBricksProvider = OrchestrationFabricInfo & {
  providerType: ORCHESTRATION_PROVIDER.Databricks | ORCHESTRATION_PROVIDER.ProphecyManaged;
};

export type OrchestrationProviderInfo = OrchestrationDataBricksProvider;

export type OrchestrationProvider<T = OrchestrationDataBricksProvider> = {
  providerInfo: T;
};

export type OrchestratorKgConfig<AuthProperties> = {
  kgConfig: {
    enabled: boolean;
    authProperties: AuthProperties;
    kgSchedule: {
      cron?: string;
      timeZone?: string;
    };
    useConnectorAuth: boolean;
    maxFiles?: number;
    includeGlobs?: string;
  };
};
export type ReachabilityStatus = 'inactive' | 'reachable' | 'unreachable' | 'unknown' | 'unsupported';

export type GenericOrchestrationConnection<
  K extends string,
  P extends Record<string, unknown>,
  isDefaultWarehouseConnection extends boolean = boolean
> = {
  id: string;
  name: string;
  kind: K;
  isDefaultWarehouseConnection: isDefaultWarehouseConnection;
  properties: P;
  reachabilityStatus: ReachabilityStatus;
};

// String - SecretPayload is used for creating unified fabrics
// It is stored as "Secret" in for orch fabrics
export type SecretPayload = Secret | string;

// -------------- DATABRICKS ORCHESTRATION TYPES START --------------

export type OrchestratorDbxAuthProperties<
  A extends OrchestrationDbxAuthType = OrchestrationDbxAuthType,
  O extends FabricOAuthType = FabricOAuthType,
  SecretPayloadType extends SecretPayload = SecretPayload
> = A extends 'pat'
  ? {
      authType: OrchestrationDbxAuthType.PAT;
      token: SecretPayloadType;

      oAuthType?: never;
      clientId?: never;
      clientSecret?: never;

      oAuthAppRegistrationID?: never;
      authServerUrl?: never;
      resource?: never;
      certificate?: never;
      privateKey?: never;
    }
  : A extends OrchestrationDbxAuthType.OIDC
    ? OrchestratorOidcAuthProperties<SecretPayloadType> & {
        authType: OrchestrationDbxAuthType.OIDC;

        token?: never;
        oAuthType?: never;
        oAuthAppRegistrationID?: never;
      }
    : O extends FabricOAuthType.M2M
      ? {
          authType: OrchestrationDbxAuthType.OAUTH;
          oAuthType: FabricOAuthType.M2M;
          clientId: string;
          clientSecret: SecretPayloadType;

          token?: never;
          oAuthAppRegistrationID?: never;
          authServerUrl?: never;
          resource?: never;
          certificate?: never;
          privateKey?: never;
        }
      : {
          authType: OrchestrationDbxAuthType.OAUTH;
          oAuthType: FabricOAuthType.U2M;
          oAuthAppRegistrationID: string;

          token?: never;
          clientId?: never;
          clientSecret?: never;
          authServerUrl?: never;
          resource?: never;
          certificate?: never;
          privateKey?: never;
        };

export type DatabricksConnectionProperties<SecretPayloadType extends SecretPayload = Secret> =
  OrchestratorDbxAuthProperties<OrchestrationDbxAuthType, FabricOAuthType, SecretPayloadType> &
    // the KG indexer is always OAuth, never PAT or OIDC
    OrchestratorKgConfig<
      OrchestratorDbxAuthProperties<OrchestrationDbxAuthType.OAUTH, FabricOAuthType, SecretPayloadType>
    > & {
      jdbcUrl: string;
      schema: string;
      catalog: string;
      clusterId: string;
    };

export type DatabricksDefaultWarehouseConnection<SecretPayloadType extends SecretPayload = Secret> =
  GenericOrchestrationConnection<
    ORCHESTRATION_PROVIDER.Databricks,
    DatabricksConnectionProperties<SecretPayloadType>,
    true
  >;

// -------------- DATABRICKS ORCHESTRATION TYPES END --------------

// -------------- BIGQUERY ORCHESTRATION TYPES START --------------

export enum OrchestrationBQAuthType {
  PrivateKey = 'private_key',
  OAuth = 'oauth'
}

export type OrchestratorBQAuthProperties<
  A extends OrchestrationBQAuthType = OrchestrationBQAuthType,
  SecretPayloadType extends SecretPayload = SecretPayload
> = A extends OrchestrationBQAuthType.PrivateKey
  ? {
      authType: OrchestrationBQAuthType.PrivateKey;
      serviceAccountKey: SecretPayloadType;

      oAuthAppRegistrationID?: never;
    }
  : {
      authType: OrchestrationBQAuthType.OAuth;
      oAuthAppRegistrationID: string;

      serviceAccountKey?: never;
    };

export type BigQueryConnectionProperties<SecretPayloadType extends SecretPayload = Secret> =
  OrchestratorBQAuthProperties<OrchestrationBQAuthType, SecretPayloadType> &
    OrchestratorKgConfig<OrchestratorBQAuthProperties<OrchestrationBQAuthType, SecretPayloadType>> & {
      projectId: string;
      dataset: string;
      provider: string;
      bucket: string;
      kmskey: string;
    };

export type BigQueryDefaultWarehouseConnection<SecretPayloadType extends SecretPayload = Secret> =
  GenericOrchestrationConnection<
    ORCHESTRATION_PROVIDER.BigQuery,
    BigQueryConnectionProperties<SecretPayloadType>,
    true
  >;

// -------------- BIGQUERY ORCHESTRATION TYPES END --------------

// -------------- SNOWFLAKE ORCHESTRATION TYPES START --------------

export enum OrchestrationSnowflakeAuthType {
  PWD = 'pwd',
  OAUTH = 'oauth',
  KEY_PAIR = 'keypair',
  OIDC = 'oidc'
}

export type OrchestratorSnowflakeAuthProperties<
  A extends OrchestrationSnowflakeAuthType = OrchestrationSnowflakeAuthType,
  SecretPayloadType extends SecretPayload = SecretPayload
> = A extends OrchestrationSnowflakeAuthType.PWD
  ? {
      authType: OrchestrationSnowflakeAuthType.PWD;
      username: string;
      password: SecretPayloadType;

      oAuthType?: never;
      oAuthAppRegistrationID?: never;
      privateKey?: never;
      authServerUrl?: never;
      resource?: never;
      clientId?: never;
      clientSecret?: never;
      certificate?: never;
    }
  : A extends OrchestrationSnowflakeAuthType.KEY_PAIR
    ? {
        authType: OrchestrationSnowflakeAuthType.KEY_PAIR;
        username: string;
        privateKey: SecretPayloadType;

        password?: never;
        oAuthType?: never;
        oAuthAppRegistrationID?: never;
        authServerUrl?: never;
        resource?: never;
        clientId?: never;
        clientSecret?: never;
        certificate?: never;
      }
    : A extends OrchestrationSnowflakeAuthType.OIDC
      ? OrchestratorOidcAuthProperties<SecretPayloadType> & {
          authType: OrchestrationSnowflakeAuthType.OIDC;
          // stored on the connection like the other Snowflake auth types, so a second admin
          // opening the fabric still sees it
          username: string;

          password?: never;
          oAuthType?: never;
          oAuthAppRegistrationID?: never;
        }
      : {
          authType: OrchestrationSnowflakeAuthType.OAUTH;
          oAuthType: FabricOAuthType.U2M;
          oAuthAppRegistrationID: string;

          username?: never;
          password?: never;
          privateKey?: never;
          authServerUrl?: never;
          resource?: never;
          clientId?: never;
          clientSecret?: never;
          certificate?: never;
        };

export type SnowflakeConnectionProperties<SecretPayloadType extends SecretPayload = Secret> =
  OrchestratorSnowflakeAuthProperties<OrchestrationSnowflakeAuthType, SecretPayloadType> & {
    account: string;
    database: string;
    schema: string;
    warehouse: string;
    role: string;
  };

export type SnowflakeDefaultWarehouseConnection<SecretPayloadType extends SecretPayload = Secret> =
  GenericOrchestrationConnection<
    ORCHESTRATION_PROVIDER.Snowflake,
    SnowflakeConnectionProperties<SecretPayloadType>,
    true
  > & {
    category: 'warehouse';
  };

// -------------- SNOWFLAKE ORCHESTRATION TYPES END --------------

export type OrchestrationDefaultWarehouseConnection<
  K extends ORCHESTRATION_PROVIDER = ORCHESTRATION_PROVIDER,
  SecretPayloadType extends SecretPayload = Secret
> = K extends ORCHESTRATION_PROVIDER.Databricks
  ? DatabricksDefaultWarehouseConnection<SecretPayloadType>
  : K extends ORCHESTRATION_PROVIDER.BigQuery
    ? BigQueryDefaultWarehouseConnection<SecretPayloadType>
    : K extends ORCHESTRATION_PROVIDER.Snowflake
      ? SnowflakeDefaultWarehouseConnection<SecretPayloadType>
      : never;
